/**
 * BlockquoteButton Component
 * 
 * A fully accessible blockquote button for Tiptap editors.
 * Easily wrap selected content in a <blockquote> with keyboard shortcut
 * support and flexible customization options.
 * 
 * Features:
 * - Toggle blockquote formatting
 * - Keyboard shortcut support (Cmd/Ctrl + Shift + B)
 * - Accessible with proper ARIA attributes
 * - Optional shortcut badge display
 * - Callback on toggle
 */

import React from 'react';
import { Editor } from '@tiptap/react';
import { useBlockquote, canToggleBlockquote, toggleBlockquote, BlockQuoteIcon } from './useBlockquote';

export interface BlockquoteButtonProps {
    /** The Tiptap editor instance */
    editor: Editor | null;
    /** Optional text label for the button */
    text?: string;
    /** Hides the button when blockquote is not applicable */
    hideWhenUnavailable?: boolean;
    /** Callback fired after a successful toggle */
    onToggled?: () => void;
    /** Shows a keyboard shortcut badge (if available) */
    showShortcut?: boolean;
    /** Additional className for the button */
    className?: string;
}

/**
 * Parse shortcut keys for display
 */
function parseShortcutKeys(keys: string[]): string {
    return keys.join(' + ');
}

/**
 * A prebuilt React component that toggles blockquote formatting.
 */
export function BlockquoteButton({
    editor,
    text,
    hideWhenUnavailable = false,
    onToggled,
    showShortcut = false,
    className,
}: BlockquoteButtonProps) {
    const {
        isVisible,
        isActive,
        canToggle,
        handleToggle,
        label,
        shortcutKeys,
        Icon,
    } = useBlockquote({
        editor,
        hideWhenUnavailable,
        onToggled,
    });

    if (!isVisible) {
        return null;
    }

    return (
        <button
            type="button"
            className={`tiptap-btn blockquote-btn ${isActive ? 'active' : ''} ${className || ''}`}
            onClick={handleToggle}
            disabled={!canToggle}
            aria-label={label}
            aria-pressed={isActive}
            title={`${label}${showShortcut ? ` (${parseShortcutKeys(shortcutKeys)})` : ''}`}
        >
            <Icon className="w-4 h-4" />
            {text && <span className="btn-text">{text}</span>}
            {showShortcut && shortcutKeys && (
                <span className="shortcut-badge">
                    {parseShortcutKeys(shortcutKeys)}
                </span>
            )}
        </button>
    );
}

// Re-export utilities and hook for convenient imports
export { useBlockquote, canToggleBlockquote, toggleBlockquote, BlockQuoteIcon };

export default BlockquoteButton;
